import { tokenService } from './token-service'
import { createLoginFailedError, createSessionExpiredError, isAuthError } from './auth-errors'
import { appraiserBackendEndpoints, identityServiceEndpoints } from './auth-client.http'
import { IframeAuthProxy } from './frame-proxy'
import authLogger from './auth-logger'

function parseAuthResponse(response) {
  return {
    accessToken: response.token || response.access_token,
    expiresAt: response.expires_at ?? response.expires,
    expiresIn: response.expires_in,
  }
}

/**
 * AuthClient handles core authentication operations for the appraiser frontend.
 * It owns token lifecycle (set/remove) but NOT user data — that lives in Redux.
 *
 * When `useFrame` is true, operations route through the iframe auth proxy to
 * the identity service. When false, they call the legacy appraiser backend.
 */
export class AuthClient {
  constructor(useFrame = false) {
    this.useFrame = useFrame
    this.proxy = useFrame ? new IframeAuthProxy() : null
  }

  /**
   * Validate the current session.
   * - useFrame: calls identity service STATUS via proxy, gets a fresh token
   * - legacy: decodes the mounted JWT and checks expiry
   */
  checkSession = async () => {
    authLogger.debug('checking session:', { useFrame: this.useFrame })

    if (this.useFrame) {
      try {
        const mountedToken = tokenService.getToken()
        const { accessToken, expiresIn } = parseAuthResponse(
          await identityServiceEndpoints.STATUS(this.proxy, mountedToken),
        )
        tokenService.setToken(accessToken)
        return { accessToken, expiresIn }
      } catch (error) {
        tokenService.removeToken()
        if (isAuthError(error)) throw error
        if (error.status === 401 || error.status === 403) {
          throw createLoginFailedError()
        }
        authLogger.error('check session failed:', error)
        throw createLoginFailedError()
      }
    }

    try {
      const mountedToken = tokenService.getToken()
      if (!mountedToken || !tokenService.isTokenValid(mountedToken)) {
        tokenService.removeToken()
        throw createLoginFailedError()
      }
      return { accessToken: mountedToken }
    } catch (error) {
      tokenService.removeToken()
      if (isAuthError(error)) throw error
      throw createLoginFailedError()
    }
  }

  /**
   * Log in with username and password.
   * - useFrame: routes through identity service via proxy
   * - legacy: calls appraiser backend POST /api/auth
   */
  login = async ({ username, password }) => {
    authLogger.debug('login from credentials:', { useFrame: this.useFrame })

    if (this.useFrame) {
      const result = await identityServiceEndpoints.LOGIN(this.proxy, username, password)
      const { accessToken, expiresIn } = parseAuthResponse(result)
      tokenService.setToken(accessToken)
      return { accessToken, expiresIn }
    }

    const result = await appraiserBackendEndpoints.LOGIN(username, password)
    const { accessToken } = parseAuthResponse(result)
    tokenService.setToken(accessToken)
    return { accessToken }
  }

  /**
   * Log in with a login code from the identity service.
   * Requires useFrame — codes are only issued by the identity service.
   */
  loginWithCode = async (code) => {
    authLogger.debug('login with code:', { useFrame: this.useFrame })

    if (!this.useFrame) {
      throw createLoginFailedError('Login codes require identity service')
    }

    const result = await identityServiceEndpoints.LOGIN_CODE(this.proxy, code)
    const { accessToken, expiresIn } = parseAuthResponse(result)
    tokenService.setToken(accessToken)
    return { accessToken, expiresIn }
  }

  /**
   * Log in with a pre-signed impersonation token.
   * - useFrame: validates token via identity service LOGIN_TOKEN
   * - legacy: stores token directly
   */
  impersonationLogin = async (token) => {
    authLogger.debug('impersonation login:', { useFrame: this.useFrame })

    if (this.useFrame) {
      const result = await identityServiceEndpoints.LOGIN_TOKEN(this.proxy, token)
      const { accessToken, expiresIn } = parseAuthResponse(result)
      tokenService.setToken(accessToken)
      return { accessToken, expiresIn }
    }

    tokenService.setToken(token)
    return { accessToken: token }
  }

  /**
   * Refresh the bearer token.
   * - useFrame: calls identity service TOKEN via proxy
   * - legacy: no-op (backend doesn't support refresh)
   */
  refreshToken = async () => {
    if (this.useFrame) {
      authLogger.debug('refreshing token via proxy')
      try {
        const result = await identityServiceEndpoints.TOKEN(this.proxy)
        const { accessToken, expiresIn } = parseAuthResponse(result)
        tokenService.setToken(accessToken)
        return { accessToken, expiresIn }
      } catch (error) {
        if (error.status === 401 || error.status === 403) {
          tokenService.removeToken()
          throw createSessionExpiredError()
        }
        throw error
      }
    }

    authLogger.debug('refreshToken: no-op (legacy backend)')
    return null
  }

  /**
   * Log out.
   * - useFrame: calls both identity service and legacy backend logout
   * - legacy: calls legacy backend only
   */
  logout = async () => {
    authLogger.debug('logging out:', { useFrame: this.useFrame })
    const currentBearerToken = tokenService.getToken()

    try {
      if (this.useFrame) {
        await identityServiceEndpoints.LOGOUT(this.proxy, currentBearerToken).catch((error) => {
          authLogger.error('identity service logout failed:', error)
        })
      }
      await appraiserBackendEndpoints.LOGOUT(currentBearerToken)
    } catch (error) {
      authLogger.error('logout failed:', error)
    } finally {
      tokenService.removeToken()
    }
  }

  destroy() {
    if (this.proxy) {
      this.proxy.destroy()
      this.proxy = null
    }
  }
}
